import { Injectable } from '@angular/core';
import { BaseService } from '../base.service';
import environment from '../../../../environments/environment';
import { Constants } from '../../utilities/constants';
import { StringUtilities } from '../../utilities/string-utilities';
import { HttpUtilityService } from '../http-utility/http-utility.service';
import { LogOnOptions } from './logon-options';
import _ from 'lodash';
import { Observable, ReplaySubject, of, EMPTY } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { switchMap, finalize, catchError } from 'rxjs/operators';
import { PlatformSessionService } from '../platform-service/session/platform-session.service';
import { SiteNavigationLinksService } from '../site-navigation/site-navigation-links.service';
import { BasespaceService } from '@bssh/ng-sdk';
import { ConsoleLogger } from '@app/core/utilities/consolelogger';
export interface IAuthorizationService {
  logon(logOnOptions: LogOnOptions): void;
  logout(force: boolean, logOnOptions: LogOnOptions): void;
  validateUserSession(): Observable<boolean>;
  getPsToken(): string;
  getDomainNameForCurrentLoggedInUser(): string;
  checkIfRequestedPathIsPublic(requestedPath: string): boolean;
}

/**
 * An Authorization service which provides auth actions such login, logout and session validation
 */
@Injectable({
  providedIn: 'root'
})
export class AuthorizationService extends BaseService implements IAuthorizationService {

  private isAuthorizedSubject = new ReplaySubject<boolean>(1);

  /**
   * An Observable which indicates whether the user is authorized
   */
  public isAuthorized$ = this.isAuthorizedSubject.asObservable();

  constructor(private cookieService: CookieService,
              private httpUtilityService: HttpUtilityService,
              private platformSessionService: PlatformSessionService,
              private siteNavigationLinksService: SiteNavigationLinksService,
              private basespaceApi: BasespaceService) {
    super();
  }

  /**
   * Handles user log on
   * @param logOnOptions Login options
   */
  logon(logOnOptions: LogOnOptions = { returnUrl: this.httpUtilityService.getCurrentRequestUrl(), uiMode: null, deviceType: null }) {
    const psToken = this.getPsToken();
    this.subs.sink = this.validateUserSession().subscribe({
      next: (isSessionValid) => {
        // If psToken cookie is not present
        // Or if the session has expired on the platform side, redirect to login.
        if (StringUtilities.isBlank(psToken) || !isSessionValid) {
          this.tryRedirectToPlatformLoginPage(logOnOptions);
        } else {
          // user has a cookie and it is valid.
          this.isAuthorizedSubject.next(isSessionValid);
        }
      }
    });
  }
  /**
   * Handle user log out
   * @param force Force logout by redirecting to platform login page, even though the user may be on one of the publicly accesible pages
   * @param logOnOptions The logon options
   */
  // tslint:disable-next-line: max-line-length
  logout(force: boolean = false, logOnOptions: LogOnOptions = { returnUrl: this.httpUtilityService.getCurrentRequestUrl(), uiMode: null, deviceType: null }): void {

    const psToken = this.getPsToken();

    if (!StringUtilities.isBlank(psToken)) {
       // Todo: remove Platform logout logic once we ascertain that BSSH api is stable for a release.
      if (environment.useBsshSessionInvalidate) {
        ConsoleLogger.logMessage('Using BSSH Session Invalidate API');
        // Call out to BSSH API to invalidate User Session on Platform + Update Login session
        this.subs.sink = this.basespaceApi.DeleteV2SessionInvalidate().pipe(
          finalize(() => {
            // Delete psToken cookie locally
            this.deletePsTokenCookie();
            // Redirect to platform login.
            this.tryRedirectToPlatformLoginPage(logOnOptions, force);
          })
        ).subscribe();

        return;
      }
      ConsoleLogger.logMessage('Using Platform Session Invalidate API');
      // Call out to platform to invalidate the pstoken.
      this.subs.sink = this.platformSessionService.invalidateSessionToken(psToken).pipe(
        finalize(() => {
          // Delete psToken cookie locally
          this.deletePsTokenCookie();
          // Redirect to platform login.
          this.tryRedirectToPlatformLoginPage(logOnOptions, force);
        })
      ).subscribe();
    } else {
      // If there is no cookie, just redirect.
      this.tryRedirectToPlatformLoginPage(logOnOptions, force);
    }

  }

  /**
   * Checks if the current user session is active via Platform's authenticate endpoint.
   * Handles EULA enforcement by redirecting to the Platform EULA acceptance page if needed.
   */
  validateUserSession(): Observable<boolean> {
    const psToken = this.getPsToken();
    if (StringUtilities.isBlank(psToken)) {
      this.isAuthorizedSubject.next(false);
      return of(false);
    }

    return this.platformSessionService.authenticate(psToken).pipe(
      switchMap((result) => {
        if (result.eulaRequired && result.eulaAcceptanceUrl) {
          const eulaUrl = this.appendClientVarsIfMissing(result.eulaAcceptanceUrl);
          this.httpUtilityService.redirectToUrl(eulaUrl);
          return EMPTY;
        }
        this.isAuthorizedSubject.next(result.authenticated);
        return of(result.authenticated);
      }),
      catchError(() => {
        return of(false);
      })
    );
  }

  /**
   * Appends clientVars (base64-encoded full return URL) to the EULA acceptance URL
   * if not already present. Platform uses clientVars to redirect the user back to BSSH
   * after EULA acceptance.
   *
   * Workaround: Platform's auth/authenticate response does not include clientVars in
   * the eula-acceptance-url. A separate bug has been filed for Platform to include it.
   * Once Platform starts including clientVars, the early-return check below will
   * prevent duplication.
   *
   * The EULA acceptance URL currently uses an AngularJS hash-based route, e.g.:
   *   .../?clientId=basespace#/showEula?key=abc
   * clientVars must be appended as a query parameter *before* the # fragment,
   * otherwise it becomes part of the hash and is never sent to the server.
   * If Platform later drops the # fragment, this logic still works correctly
   * since it simply appends clientVars as a regular query parameter.
   */
  private appendClientVarsIfMissing(eulaAcceptanceUrl: string): string {
    if (eulaAcceptanceUrl.includes('clientVars=')) {
      return eulaAcceptanceUrl;
    }

    const returnUrl = this.httpUtilityService.getCurrentRequestUrl();
    const rUrl = new URL(returnUrl);
    let clientVars = StringUtilities.toBase64String(rUrl.href).replace('+', '-').replace('/', '_');
    clientVars = _.trimEnd(clientVars, '=');

    // Split on # so clientVars is a real query param, not part of the fragment.
    // When there is no # fragment, hashFragment is empty and the result is just
    // baseUrl + delimiter + clientVars.
    const hashIndex = eulaAcceptanceUrl.indexOf('#');
    let baseUrl: string;
    let hashFragment = '';
    if (hashIndex !== -1) {
      baseUrl = eulaAcceptanceUrl.substring(0, hashIndex);
      hashFragment = eulaAcceptanceUrl.substring(hashIndex);
    } else {
      baseUrl = eulaAcceptanceUrl;
    }

    const delimiter = baseUrl.includes('?') ? '&' : '?';
    return `${baseUrl}${delimiter}clientVars=${clientVars}${hashFragment}`;
  }

  /**
   * Generates a platform logon url
   * @param returnUrl The Url to redirect upon successful logon
   * @param uiMode The Ui Mode
   * @param deviceType The Device Type
   */
  private generateLogonPlatformUrl(returnUrl?: string, uiMode?: string, deviceType?: string) {
    const platformBaseUrl = environment.platformUrl;
    const clientId = environment.clientId;
    const baseUrl = this.httpUtilityService.getBaseUrlFromCurrentRequestUrl();
    // If no return Url is specified, default to the base url.
    const rUrl = new URL(StringUtilities.isBlank(returnUrl) ? baseUrl : returnUrl);

    // assign Client vars
    let clientVars = '';
    clientVars = StringUtilities.toBase64String(rUrl.href).replace('+', '-').replace('/', '_');
    clientVars = _.trimEnd(clientVars, '=');

    const delimiter = platformBaseUrl.includes('?') ? '&' : '?';

    const options: string[] = [];
    if (!StringUtilities.isBlank(uiMode)) {
      options.push(`uiMode=${uiMode}`);
    }

    if (!StringUtilities.isBlank(deviceType)) {
      options.push(`deviceType=${deviceType}`);
    }
    const displayOptions = _.join(options, '&');

    // tslint:disable-next-line: max-line-length
    return `${platformBaseUrl}${delimiter}rURL=${baseUrl}&clientId=${clientId}&clientVars=${clientVars}&redirectMethod=GET${displayOptions}`;

  }

  getPsToken(): string {
    return this.cookieService.get(Constants.Auth.authpsToken);
  }

  /**
   * Deletes pstoken cookie and calls out to Platform to invalidate session token on the server.
   */
  private deletePsTokenCookie() {
    this.cookieService.delete(Constants.Auth.authpsToken, '/', environment.psTokenDomain);
  }

  /**
   * Redirects to Platform's login page.
   */
  // tslint:disable-next-line: max-line-length
  private tryRedirectToPlatformLoginPage(logOnOptions: LogOnOptions = { returnUrl: null, uiMode: null, deviceType: null }, force: boolean = false) {
    // If the requested page is public, do not redirect to platform logon page.
    if (!this.checkIfRequestedPathIsPublic() || force) {
      const platformLogonUrl = this.generateLogonPlatformUrl(logOnOptions.returnUrl, logOnOptions.uiMode, logOnOptions.deviceType);
      // User is not logged in, redirect to platform login.
      this.httpUtilityService.redirectToUrl(platformLogonUrl);
    } else {
      this.isAuthorizedSubject.next(true);
    }
  }

  /**
   * Gets the domain name for the currently logged in user
   */
  getDomainNameForCurrentLoggedInUser(): string {
    return this.httpUtilityService.getDomainFromPsToken(this.getPsToken());
  }


  /**
   * Detremines if the requested page path can be accessed without authorization.
   * @param requestedPath Optional. The requested path to check.
   * The current request path is considered if no value is supplied
   */
  public checkIfRequestedPathIsPublic(requestedPath?: string): boolean {
    const requestedUrlPath = requestedPath ? requestedPath : this.httpUtilityService.getPathFromCurrentRequestUrl();
    return this.siteNavigationLinksService.noAuthPageUrlsRegex.test(requestedUrlPath);
  }

}
